// Mutation queue (offline mode, Step 4).
//
// Goal: when the user makes a write (create/edit/delete a gig, update
// settings, etc.) while offline, we
//   (a) optimistically update the local cache so the UI reflects the change,
//   (b) enqueue the request to mutation_queue so the sync engine (Step 5)
//       can replay it once we're back online.
//
// We sit on top of `apiClient`'s response error interceptor, NOT inside it,
// because the existing interceptor in offlineInterceptors.js handles GETs and
// we don't want to entangle the two flows. Instead App.js attaches a second
// error interceptor that delegates to enqueueIfOffline().

import {
  STORE,
  _internal,
  putEvents,
  getAllEvents,
} from "./offlineCache";
import { putResponse, getResponse } from "./offlineCache";

const tempId = (prefix) =>
  `local-${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Append a freshly-created entity to the cached list under `/<collection>`
// so subsequent picker reads include it. Best-effort.
const appendToListCache = async (collection, item) => {
  try {
    const cached = await getResponse(`/${collection}`);
    const list = Array.isArray(cached?.data) ? cached.data : [];
    await putResponse(`/${collection}`, [...list, item]);
  } catch {}
};

const isWrite = (method) => {
  const m = (method || "get").toLowerCase();
  return m === "post" || m === "put" || m === "patch" || m === "delete";
};

// Mutations we DON'T queue — replaying them is meaningless or dangerous.
const isQueueable = (config) => {
  if (!config) return false;
  if (config.skipOfflineQueue) return false;
  const url = config.url || "";
  // Auth flows: replaying a stale magic-link or password-reset POST is a
  // security/UX hazard — let those fail loudly.
  if (/^\/auth\//.test(url)) return false;
  // Calendar OAuth disconnect/sync should be user-initiated online.
  if (/^\/calendar\/(auth-url|disconnect|sync-all)/.test(url)) return false;
  // Invoice push-to-accounting / email send — never replay automatically,
  // duplicates would be visible to clients.
  if (/\/(push-to-freeagent|push-to-xero|push-to-quickbooks|email|send-reminder)$/.test(url)) return false;
  // Subscription / payment flows.
  if (/^\/subscription\//.test(url)) return false;
  return true;
};

// --- enqueue ---------------------------------------------------------------

export const enqueueMutation = async (config) => {
  const db = await _internal.getDB();
  if (!db) return null;
  const record = {
    method: (config.method || "post").toLowerCase(),
    url: config.url || "",
    data: config.data ?? null,
    params: config.params ?? null,
    headers: sanitizeHeaders(config.headers),
    createdAt: Date.now(),
    attempts: 0,
    // Step 5 will use this to detect server-changed-since conflicts.
    baseVersion: config.__baseVersion ?? null,
    // For UI rollback if the sync ultimately fails.
    optimisticPatch: config.__optimisticPatch ?? null,
  };
  try {
    const id = await db.add(STORE.MUTATIONS, record);
    return { id, ...record };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[mutationQueue] enqueue failed:", err?.message);
    return null;
  }
};

const sanitizeHeaders = (h) => {
  if (!h || typeof h !== "object") return {};
  // Strip the iOS-Safari Bearer header — when we replay later the token may
  // have rotated; let the interceptor re-attach a fresh one.
  const { Authorization, authorization, ...rest } = h;
  return rest;
};

// --- optimistic patches ----------------------------------------------------
//
// For the endpoints we know well (gigs/events), apply a local mutation to
// IndexedDB so the UI sees the change immediately. Caller can also supply
// `config.__optimisticPatch = { store, action, payload }` to override.

export const applyOptimisticUpdate = async (config) => {
  const url = config.url || "";
  const method = (config.method || "post").toLowerCase();
  const body = config.data && typeof config.data === "object" ? config.data : null;

  try {
    // POST /gigs — create a new gig
    if (method === "post" && /^\/gigs\/?$/.test(url) && body) {
      const id = tempId("gig");
      const optimistic = { ...body, id, _pending: true, _pendingOp: "create" };
      await putEvents([optimistic]);
      return { tempId: id, entity: optimistic };
    }
    // PUT /gigs/:id — update
    const putMatch = method === "put" && url.match(/^\/gigs\/([^/?]+)$/);
    if (putMatch && body) {
      const id = putMatch[1];
      const existing = (await getAllEvents()).find((e) => e.id === id) || { id };
      const updated = { ...existing, ...body, id, _pending: true, _pendingOp: "update" };
      await putEvents([updated]);
      return { id, entity: updated };
    }
    // DELETE /gigs/:id
    const delMatch = method === "delete" && url.match(/^\/gigs\/([^/?]+)$/);
    if (delMatch) {
      const id = delMatch[1];
      const existing = (await getAllEvents()).find((e) => e.id === id);
      if (existing) {
        await putEvents([{ ...existing, _pending: true, _pendingOp: "delete" }]);
      }
      return { id };
    }
    // POST /bands — synthesize { band_id, name, ... } so the caller can use it
    // (e.g. immediately attach the new band to a gig).
    if (method === "post" && /^\/bands\/?$/.test(url) && body) {
      const id = tempId("band");
      const optimistic = { ...body, band_id: id, _pending: true, _pendingOp: "create" };
      await putResponse(`/bands/${id}`, optimistic);
      await appendToListCache("bands", optimistic);
      return { id, entity: optimistic };
    }
    // POST /collaborators — synthesize { collaborator_id, name, ... }
    if (method === "post" && /^\/collaborators\/?$/.test(url) && body) {
      const id = tempId("collab");
      const optimistic = { ...body, collaborator_id: id, _pending: true, _pendingOp: "create" };
      await putResponse(`/collaborators/${id}`, optimistic);
      await appendToListCache("collaborators", optimistic);
      return { id, entity: optimistic };
    }
    // POST /venues — synthesize a venue object. UI keys by name, so we make
    // sure the name passed in survives.
    if (method === "post" && /^\/venues\/?$/.test(url) && body) {
      const id = tempId("venue");
      const optimistic = { ...body, venue_id: id, _pending: true, _pendingOp: "create" };
      const key = body.name || id;
      await putResponse(`/venues/${encodeURIComponent(key)}`, optimistic);
      await appendToListCache("venues", optimistic);
      return { id, entity: optimistic };
    }
  } catch (err) {
    // Optimistic updates are best-effort. The queued mutation is the source of truth.
    // eslint-disable-next-line no-console
    console.warn("[mutationQueue] optimistic update failed:", err?.message);
  }
  return null;
};

// --- queue inspection (used by Step 5 sync engine and Step 6 UI) -----------

export const listQueue = async () => {
  const db = await _internal.getDB();
  if (!db) return [];
  try {
    return await db.getAll(STORE.MUTATIONS);
  } catch {
    return [];
  }
};

export const removeFromQueue = async (id) => {
  const db = await _internal.getDB();
  if (!db) return false;
  try {
    await db.delete(STORE.MUTATIONS, id);
    return true;
  } catch {
    return false;
  }
};

export const bumpAttempt = async (id) => {
  const db = await _internal.getDB();
  if (!db) return;
  try {
    const tx = db.transaction(STORE.MUTATIONS, "readwrite");
    const rec = await tx.store.get(id);
    if (rec) {
      rec.attempts = (rec.attempts || 0) + 1;
      rec.lastAttemptAt = Date.now();
      await tx.store.put(rec);
    }
    await tx.done;
  } catch {}
};

// --- interceptor entry point ----------------------------------------------

// Returns a response-like object for a queued mutation so callers don't crash.
// `status: 202` signals "accepted, will sync later".
// When we have a synthesized entity (POST creates), put it in `data` so the
// caller can immediately use the new id (e.g. `res.data.band_id`). Otherwise
// fall back to a generic queued envelope.
const synthesizeQueuedResponse = (config, queued, optimistic) => ({
  data: optimistic?.entity
    ? optimistic.entity
    : { queued: true, queueId: queued?.id, optimistic },
  status: 202,
  statusText: "Queued offline",
  headers: {},
  config,
  fromQueue: true,
  queueId: queued?.id,
});

export const attachMutationQueueInterceptor = (apiClient) => {
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config || {};
      // A response-less error (`!error?.response`) covers CORS preflight
      // rejections, timeouts, and a mid-request server restart, not just
      // genuine offline — none of those mean "safe to queue and pretend it
      // saved." Require navigator.onLine === false too, so a real connectivity
      // drop (which the rest of the sync engine is keyed off of, see
      // syncEngine.js) still queues and optimistically updates, but a
      // same-network failure surfaces as a real error the caller can retry.
      const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
      const isNetworkFail = !error?.response && isOffline;
      if (!isNetworkFail || !isWrite(config.method) || !isQueueable(config)) {
        return Promise.reject(error);
      }
      // Optimistically apply to local cache, then enqueue.
      const optimistic = await applyOptimisticUpdate(config);
      const queued = await enqueueMutation(config);
      if (!queued) {
        // Could not persist the queue (IDB unavailable) — let the caller
        // see the real error rather than pretending we saved it.
        return Promise.reject(error);
      }
      return synthesizeQueuedResponse(config, queued, optimistic);
    }
  );
};
